<script setup>
import { ref, reactive, watch, onMounted, computed } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { formatarDataBR } from '@/utils/useDataBR'
import { NV_MODULO_FISCAL } from '@/constants/NV_MODULO_FISCAL'

const authStore = useAuthStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)
const { obterClasseStatusGhost } = useUI()

const lista = ref([])
const isLoading = ref(false)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const filtros = reactive({ idOperacao: '', ufOrigem: '', ufDestino: '' })

const listas = reactive({
  operacoes: [],
  tiposPessoa: [],
  regimesTributarios: []
})

const registroAuditoria = ref(null)
const mostrarAuditoria = ref(false)

const emit = defineEmits(['editar-item', 'adicionar-item', 'show-notification'])

const isPlanoEssential = computed(() => authStore.lstNivelModuloFiscal === NV_MODULO_FISCAL.Essential)

const podeVerAcoes = computed(() => authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('FISCAL_MATRIZ_COMPLETO')))
const temPermissaoCompleta = computed(() => podeVerAcoes.value)

const colunas = computed(() => {
  const cols = [
    { field: 'idMatrizFiscal', label: 'ID', width: 80, sortable: true, slotName: 'id' },
    { field: 'ufOrigem', label: 'Origem ➔ Destino', align: 'center', sortable: true, slotName: 'uf' },
    { field: 'codigoNcm', label: 'NCM (Prefixo/Exato)', align: 'center', sortable: true, slotName: 'ncm' },
    { field: 'lstRegimeTributario', label: 'Parceiro', align: 'center', sortable: true, slotName: 'parceiro' },
    { field: 'operacao', label: 'Operação', sortable: true, slotName: 'operacao' },
    { field: 'perfilTributario', label: 'Perfil Resultante', sortable: true, slotName: 'perfil' },
    { field: 'dataValidadeInicio', label: 'Status / Vigência', align: 'center', sortable: true, slotName: 'vigencia' }
  ]
  if (podeVerAcoes.value) {
    cols.push({ field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false })
  }
  return cols
})

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null
  const resolvers = {
    lstRegimeTributario: (row) => getListaTexto(row.lstRegimeTributario, listas.regimesTributarios)
  }
  const resolver = resolvers[field]
  if (!resolver) return null
  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => { buscarDados(1) }

async function carregarListas () {
  try {
    const [resOps, resTipos, resRegimes] = await Promise.allSettled([
      api.get('Operacoes?PageSize=500'),
      api.get('/ListasSistema/lookup/PARCEIRO_TIPO'),
      api.get('/ListasSistema/lookup/REGIME_TRIBUTARIO')
    ])
    if (resOps.status === 'fulfilled') listas.operacoes = resOps.value.data
    if (resTipos.status === 'fulfilled') listas.tiposPessoa = resTipos.value.data
    if (resRegimes.status === 'fulfilled') listas.regimesTributarios = resRegimes.value.data
  } catch (e) { console.warn('Erro ao carregar listas:', e) }
}

function getListaTexto (id, lista) {
  if (!id) return '-'
  const obj = lista.find(l => l.idListaSistemaItem === id || l.idListaClienteItem === id)
  return obj ? obj.textoLista : id
}

function obterStatusVigencia (dataInicio, dataFim) {
  const hoje = new Date()
  if (dataFim && new Date(dataFim) < hoje) return 'Expirado'
  if (dataInicio && new Date(dataInicio) > hoje) return 'Programado'
  return 'Vigente'
}

async function buscarDados (pageNumber = 1) {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) { lista.value = []; return }
  isLoading.value = true
  const params = injetarParametros(pageNumber)
  if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
  if (filtros.idOperacao) params.append('idOperacao', filtros.idOperacao)
  if (filtros.ufOrigem) params.append('ufOrigem', filtros.ufOrigem)
  if (filtros.ufDestino) params.append('ufDestino', filtros.ufDestino)
  if (sortField.value) params.append('sortField', sortField.value)
  if (sortOrder.value) params.append('sortOrder', sortOrder.value)

  try {
    const response = await api.get(`MatrizesFiscais?${params.toString()}`)
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro:', error)
    if (error.response?.status === 403) {
      const msg = error.response.data?.message || 'Acesso negado. O plano da empresa não permite esta funcionalidade.'
      emit('show-notification', { message: msg, type: 'danger' })
    } else if (error.response?.status === 401) {
      emit('show-notification', { message: 'Sessão expirada. Faça login novamente.', type: 'warning' })
    }
  } finally { isLoading.value = false }
}

function aplicarFiltros () { buscarDados(1) }
function limparFiltros () { filtros.idOperacao = ''; filtros.ufOrigem = ''; filtros.ufDestino = ''; buscarDados(1) }
function novoItem () { emit('adicionar-item') }
function editarItem (item) { emit('editar-item', item) }
function abrirAuditoria (item) {
  registroAuditoria.value = item
  mostrarAuditoria.value = true
}

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    listas.operacoes = []
    listas.tiposPessoa = []
    listas.regimesTributarios = []
  }
})

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})
defineExpose({ buscarDados })
</script>

<template>
  <div v-if="isPlanoEssential" class="text-center p-5">
    <i class="bi bi-lock-fill display-4 text-muted"></i>
    <p class="mt-3 text-muted">Funcionalidade disponível apenas nos planos Avançado ou Enterprise.</p>
  </div>

  <PaginaAdministrar
    v-else
    titulo="Matriz Fiscal (Motor de Regras)"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="buscarDados"
    @mudar-tamanho-pagina="buscarDados(1)"
    @adicionar="novoItem"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="temPermissaoCompleta"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3">
        <div class="col-md-4">
          <select class="form-select" v-model="filtros.idOperacao" @change="aplicarFiltros">
            <option value="">Todas as Operações</option>
            <option v-for="op in listas.operacoes" :key="op.idOperacao" :value="op.idOperacao">{{ op.descricao }}</option>
          </select>
        </div>
        <div class="col-md-2">
          <input class="form-control text-uppercase" placeholder="UF Orig. (Ex: SP)" maxlength="2" v-model="filtros.ufOrigem" @keyup.enter="aplicarFiltros">
        </div>
        <div class="col-md-2">
          <input class="form-control text-uppercase" placeholder="UF Dest. (Ex: RJ)" maxlength="2" v-model="filtros.ufDestino" @keyup.enter="aplicarFiltros">
        </div>
        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros" :disabled="isLoading">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros" :disabled="isLoading">
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
            Aplicar Filtros
          </button>
        </div>
      </div>
    </template>

    <div v-if="isLoading && lista.length === 0" class="text-center p-5"><div class="spinner-border text-primary"></div></div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="isLoading"
      empty-message="Nenhuma regra de cruzamento fiscal encontrada para os filtros aplicados."
      row-key="idMatrizFiscal"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarItem($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idMatrizFiscal || row.id }}</span>
      </template>

      <template #operacao="{ row }">
        <span class="fw-medium text-body-emphasis">{{ row.operacaoDescricao }}</span>
      </template>

      <template #uf="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle px-3 py-2">
          {{ row.ufOrigem || '*' }} <i class="bi bi-arrow-right mx-1"></i> {{ row.ufDestino || '*' }}
        </span>
      </template>

      <template #ncm="{ row }">
        <span v-if="!row.codigoNcm && !row.ncmPrefixo" class="badge-ghost-warning fw-normal px-2 py-1 rounded">
          <i class="bi bi-asterisk small"></i> Qualquer NCM
        </span>
        <span v-else class="badge bg-info text-dark">{{ row.codigoNcm || row.ncmPrefixo }}</span>
      </template>

      <template #parceiro="{ row }">
        <div v-if="!row.lstRegimeTributario && !row.lstTipoPessoaDestino" class="text-muted small">
          <i class="bi bi-asterisk"></i> Qualquer Parceiro
        </div>
        <div v-else class="small">
          <span v-if="row.lstTipoPessoaDestino" class="fw-bold text-primary">{{ getListaTexto(row.lstTipoPessoaDestino, listas.tiposPessoa) }}</span>
          <span v-if="row.lstTipoPessoaDestino && row.lstRegimeTributario"> - </span>
          <span v-if="row.lstRegimeTributario">{{ getListaTexto(row.lstRegimeTributario, listas.regimesTributarios) }}</span>
        </div>
      </template>

      <template #perfil="{ row }">
        <span class="fw-medium text-success">{{ row.perfilTributario?.descricao || row.perfilTributarioDescricao || 'Perfil ID: ' + row.idPerfilTributario }}</span>
      </template>

      <template #vigencia="{ row }">
        <span :class="obterClasseStatusGhost(obterStatusVigencia(row.dataValidadeInicio, row.dataValidadeFim))">
          {{ obterStatusVigencia(row.dataValidadeInicio, row.dataValidadeFim) }}
        </span>
        <div v-if="row.dataValidadeInicio || row.dataValidadeFim" class="small text-muted mt-1" style="font-size: 0.75rem;">
          {{ formatarDataBR(row.dataValidadeInicio) }} <i class="bi bi-arrow-right"></i> {{ formatarDataBR(row.dataValidadeFim) }}
        </div>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1" v-if="podeVerAcoes">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico de Auditoria">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon text-primary" @click="editarItem(row)" title="Editar Regra Fiscal">
            <i class="bi bi-pencil-fill"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="MatrizFiscal"
    contexto="tenant"
    :chave-primaria="registroAuditoria?.idMatrizFiscal"
    :titulo-registro="'Regra ' + (registroAuditoria?.idMatrizFiscal || '')"
    @fechar="mostrarAuditoria = false; registroAuditoria = null"
  />
</template>

<style scoped>
</style>
